import axios, { AxiosResponse } from 'axios';

axios.defaults.baseURL = process.env.REACT_APP_PLANETDIET_API_URL;
axios.defaults.headers.post['Content-Type'] = 'text/json';

export type BookInfoMeetingAppointmentPayload = {
    email: string // Email of the customer booking the appointments
    phone: string // Phone number of the customer booking the appointments
    infoMeetingStart: string // DateTime string in format "s" for start of D0 appointment
}

export type BookV0AppointmentPayload = {
    email: string // Email of the customer booking the appointments
    phone: string // Phone number of the customer booking the appointments
    v0meetingstart: string // DateTime string in format "s" for start of D0 appointment
}

export type BookV1V2AppointmentsPayload = {
    V1MeetingStart: string // DateTime string in format "s" for start of D1 appointment
    V2MeetingStart: string // DateTime string in format "s" for start of D2 appointment
}

export type BookV3V4AppointmentsPayload = {
    V3MeetingStart: string // DateTime string in format "s" for start of D3 appointment
    V4MeetingStart: string // DateTime string in format "s" for start of D4 appointment
}

export type GetInfoMeetingStaffAvailabilityPayload = {
    year: number // Number of the year to get availability for
    month: number // Number of the month (1-12) to get availability for
}

export type GetV0MeetingStaffAvailabilityPayload = {
    year: number // Number of the year to get availability for
    month: number // Number of the month (1-12) to get availability for
}

export type GetV1V2StaffAvailabilityPayload = {
    year: number // Number of the year to get availability for
    month: number // Number of the month (1-12) to get availability for
}

export type GetV3V4StaffAvailabilityPayload = {
    year: number // Number of the year to get availability for
    month: number // Number of the month (1-12) to get availability for
}

export type StaffAvailablilityItem = {
    timeSlotStart: string // DateTime string in format "s" for timeslot start
}

export type GetInfoMeetingStaffAvailabilityResponse = {
    infoMeetingItems: StaffAvailablilityItem[]
}

export type GetV0MeetingStaffAvailabilityResponse = {
    v0MeetingItems: StaffAvailablilityItem[]
}

export type GetV1V2StaffAvailabilityResponse = {
    v1MeetingItems: StaffAvailablilityItem[]
    v2MeetingItems: StaffAvailablilityItem[]
}

export type GetV3V4StaffAvailabilityResponse = {
    v3MeetingItems: StaffAvailablilityItem[]
    v4MeetingItems: StaffAvailablilityItem[]
}

export type BookingConfirmationPageResponse = {
    title: string,
    date: string,
    duration: string,
    location: string
}

export type BookingConfirmationInfoMeetingResponse = {
    infoMeetingBookingResponseItem: BookingConfirmationPageResponse
}

export type BookingConfirmationV0Response = {
    v0MeetingBookingResponseItem: BookingConfirmationPageResponse
}

export type BookingConfirmationV1V2Response = {
    v1MeetingBookingResponseItem: BookingConfirmationPageResponse,
    v2MeetingBookingResponseItem: BookingConfirmationPageResponse
}

export type BookingConfirmationV3V4Response = {
    v3MeetingBookingResponseItem: BookingConfirmationPageResponse,
    v4MeetingBookingResponseItem: BookingConfirmationPageResponse
}

export type ServiceDetailsResponse = {
    title: string,
    description: string,
    duration: string
} | null;

export type InfoMeetingServiceDetailsResponse = ServiceDetailsResponse;

export type V0MeetingServiceDetailsResponse = ServiceDetailsResponse;

export type V1ServiceDetailsResponse = ServiceDetailsResponse;

export type V2ServiceDetailsResponse = ServiceDetailsResponse;

export type V3ServiceDetailsResponse = ServiceDetailsResponse;

export type V4ServiceDetailsResponse = ServiceDetailsResponse;

export type BookingBusinessDetailsResponse = {
    displayName: string,
    displayAddress: string,
    webSiteUrl: string
} | null;

const apiKey = process.env.REACT_APP_PLANETDIET_API_KEY;

export const BookingAPI = {
    bookInfoMeetingAppointment: async (body: BookInfoMeetingAppointmentPayload, token: string | null): Promise<BookingConfirmationInfoMeetingResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/BookInfoMeetingAppointment', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    bookV0Appointment: async (body: BookV0AppointmentPayload, token: string | null): Promise<BookingConfirmationV0Response> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/BookV0MeetingAppointment', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    bookV1V2Appointments: async (body: BookV1V2AppointmentsPayload, token: string): Promise<BookingConfirmationV1V2Response> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/BookAppointments', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    bookV3V4Appointments: async (body: BookV3V4AppointmentsPayload, token: string): Promise<BookingConfirmationV3V4Response> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/BookV3V4MeetingAppointment', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getInfoMeetingStaffV0Availability: async (body: GetInfoMeetingStaffAvailabilityPayload, token: string): Promise<GetInfoMeetingStaffAvailabilityResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/GetInfoMeetingStaffAvailability', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV0StaffAvailability: async (body: GetV0MeetingStaffAvailabilityPayload, token: string): Promise<GetV0MeetingStaffAvailabilityResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/GetV0MeetingStaffAvailability', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV1V2StaffAvailability: async (body: GetV1V2StaffAvailabilityPayload, token: string): Promise<GetV1V2StaffAvailabilityResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/GetStaffAvailability', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV3V4StaffAvailability: async (body: GetV3V4StaffAvailabilityPayload, token: string): Promise<GetV3V4StaffAvailabilityResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.post('/booking/GetV3V4MeetingStaffAvailability', body, { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getInfoMeetingServiceDetails: async (token: string): Promise<InfoMeetingServiceDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/InfoMeetingServiceDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV0ServiceDetails: async (token: string): Promise<V0MeetingServiceDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/V0MeetingServiceDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV1ServiceDetails: async (token: string): Promise<V1ServiceDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/TD1ServiceDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV2ServiceDetails: async (token: string): Promise<V2ServiceDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/TD2ServiceDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV3ServiceDetails: async (token: string): Promise<V3ServiceDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/V3MeetingServiceDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getV4ServiceDetails: async (token: string): Promise<V4ServiceDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/V4MeetingServiceDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
    getBookingBusinessDetails: async (token: string): Promise<BookingBusinessDetailsResponse> => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Ocp-Apim-Subscription-Key': apiKey,
        };

        const response = await axios.get('/booking/BookingBusinessDetails', { headers });
        if (response.status === 200) {
            return response.data;
        } else {
            return badStatusError(response);
        }
    },
};

const badStatusError = (response: AxiosResponse) => {
    throw new Error(`Invalid response: ${response.status} - ${response.data}`);
};